import React, { useEffect, useState } from "react";
import {
  useMediaQuery,
  Box,
  Button,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  TableCaption,
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  VStack,
  useToast,
  Text,
  Flex,
} from "@chakra-ui/react";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { server } from "../util/connect";
import DiagnosisTimeModal from "./modals/DiagnosisTimeModal";
import { format } from "date-fns";
import { th } from "date-fns/locale/th";
import { useAuth } from "../auth/AuthProvider";
import DiagnosisTimeDelete from "./modals/DiagnosisTimeDelete";
import { mapDayToThai } from "../util/calendar_util";
import { IoAddCircleOutline } from "react-icons/io5";

const DoctorTimeTablePage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [operation, setOperation] = useState("insert");
  const [doctor, setDoctor] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingDiagnosisTime, setEditingDiagnosisTime] = useState(null);
  const { user } = useAuth();
  const [selectedDeleteDiagnosisTime, setSelectedDiagnosisTime] =
    useState(null);
  const [isDeleteModalOpen, setDeleteModalOpen] = useState(false);
  const toast = useToast();
  const [isMobile] = useMediaQuery("(max-width: 768px)");

  useEffect(() => {
    if (id && user && user.department_id) {
      fetchDoctorData();
    }
  }, [id, user]);

  const fetchDoctorData = async () => {
    try {
      const response = await axios.post(
        `${server}/timetable/fetch_doctor_timetable_by_id`,
        {
          doctor_id: id,
          department_id: user.department_id,
        }
      );
      setDoctor(response.data);
    } catch (error) {
      console.error("Error fetching doctor data:", error);
    }
  };

  const handleDiagnosisTimeEdit = (diagnosisTime) => {
    setOperation("edit");
    setEditingDiagnosisTime(diagnosisTime);
    setIsModalOpen(true);
  };

  const handleDiagnosisTimeDelete = async () => {
    if (selectedDeleteDiagnosisTime) {
      try {
        await axios.post(`${server}/timetable/delete_doctor_timetable`, {
          diagnosis_time_id: selectedDeleteDiagnosisTime.id,
        });
        setDeleteModalOpen(false);
        fetchDoctorData();
      } catch (error) {
        console.error("Error deleting doctor:", error);
      }
    } else {
      toast({
        title: "ไม่สามารถลบข้อมูลได้",
        status: "warning",
        duration: 3000,
        isClosable: true,
      });
    }
  };

  if (!doctor) {
    return <div>Loading...</div>;
  }

  const handleOnInsertButtonClick = () => {
    setIsModalOpen(true);
    setOperation("insert");
  };

  const formatTime = (timeString) => {
    if (timeString) {
      const timeParts = timeString.split(":");
      const hours = timeParts[0];
      const minutes = timeParts[1];
      const formattedTime = `${hours}:${minutes}`;

      return formattedTime;
    }
    return timeString;
  };

  const openDeleteModal = (deleteTimetable) => {
    setSelectedDiagnosisTime(deleteTimetable);
    setDeleteModalOpen(true);
  };

  return (
    <Box p={5} display="flex" alignItems="center" justifyContent="center">
      <Box
            w={"1300px"}
            overflowX="auto"
            css={{
              "&::-webkit-scrollbar": {
                height: "6px",
              },
              "&::-webkit-scrollbar-thumb": {
                background: "#a0aec0",
              },
            }}
          >
      <VStack alignItems="stretch">
        <Breadcrumb spacing="8px" mb="4">
          <BreadcrumbItem>
            <BreadcrumbLink
              href="#"
              onClick={() => {
                navigate("/timetable");
              }}
            >
              แพทย์ทั้งหมด
            </BreadcrumbLink>
          </BreadcrumbItem>
          <BreadcrumbItem>
            <BreadcrumbLink href="#">
              {`ตารางตรวจแพทย์ ${doctor.first_name} ${doctor.last_name}`}
            </BreadcrumbLink>
          </BreadcrumbItem>
        </Breadcrumb>
        <Flex justify="end">
          <Button colorScheme="green" leftIcon={<IoAddCircleOutline/>} variant={"outline"} onClick={handleOnInsertButtonClick} mb={2}>
            เพิ่มเวลาออกตรวจ
          </Button>
        </Flex>
        {isMobile ? (
          <VStack alignItems="stretch">
            {doctor &&
              doctor.diagnosisTimes &&
              doctor.diagnosisTimes.map((diagnosisTime) => (
                <Box
                  key={diagnosisTime.id}
                  borderWidth="2px"
                  rounded="md"
                  p={2}
                >
                  <Flex borderBottom="1px solid #E2E8F0">
                    <Text fontSize="sm" p={1} bgColor={"blue.50"} width={110} fontWeight="bold" mr={2}>วันออกตรวจ:</Text>
                    <Text fontSize="sm" p={1}>{mapDayToThai(diagnosisTime.day)}</Text>
                  </Flex>
                  <Flex borderBottom="1px solid #E2E8F0">
                    <Text fontSize="sm" p={1} bgColor={"blue.50"} width={110} fontWeight="bold" mr={2}>เวลาออกตรวจ:</Text>
                    <Text fontSize="sm" p={1}>{formatTime(diagnosisTime.start_time)} - {formatTime(diagnosisTime.end_time)}</Text>
                  </Flex>
                  <Flex borderBottom="1px solid #E2E8F0">
                    <Text fontSize="sm" p={1} bgColor={"blue.50"} width={110} fontWeight="bold" mr={2}>วันที่เริ่มตรวจ:</Text>
                    <Text fontSize="sm" p={1}>
                    {diagnosisTime.start_date
                      ? format(
                          diagnosisTime.start_date,
                          "EEEE ที่ dd MMMM yyyy",
                          { locale: th }
                        )
                      : "ไม่ระบุ"}
                    </Text>
                  </Flex>
                  <Flex borderBottom="1px solid #E2E8F0">
                    <Text fontSize="sm" p={1} bgColor={"blue.50"} width={110} fontWeight="bold" mr={2}>วันที่สิ้นสุด:</Text>
                    <Text fontSize="sm" p={1}>
                    {diagnosisTime.end_date
                      ? format(
                          diagnosisTime.end_date,
                          "EEEE ที่ dd MMMM yyyy",
                          { locale: th }
                        )
                      : "ไม่ระบุ"}
                    </Text>
                  </Flex>
                  <Flex justify={"end"} mt={2}>
                    <Button
                      my={0}
                      mr={2}
                      size={"sm"}
                      onClick={() => handleDiagnosisTimeEdit(diagnosisTime)}
                    >
                      แก้ไข
                    </Button>
                    <Button
                      my={0}
                      size={"sm"}
                      colorScheme="red"
                      onClick={() => openDeleteModal(diagnosisTime)}
                    >
                      ลบ
                    </Button>
                  </Flex>
                </Box>
              ))}
            {(!doctor ||
              !doctor.diagnosisTimes ||
              doctor.diagnosisTimes.length <= 0) && (
              <Box borderWidth="1px" rounded="md" p={4}>
                ยังไม่มีข้อมูล
              </Box>
            )}
          </VStack>
        ) : (
          
            <Table className="custom-table">
              <TableCaption>{`ตารางออกตรวจ แพทย์. ${doctor.first_name} ${doctor.last_name}`}</TableCaption>
              <Thead>
                <Tr>
                  <Th>วันที่ออกตรวจ</Th>
                  <Th>เวลาออกตรวจ</Th>
                  <Th>เวลาสิ้นสุด</Th>
                  <Th>วันที่เริ่มออกตรวจ</Th>
                  <Th>วันที่สิ้นสุดการตรวจ</Th>
                  <Th>Actions</Th>
                </Tr>
              </Thead>
              <Tbody>
                {doctor &&
                  doctor.diagnosisTimes &&
                  doctor.diagnosisTimes.map((diagnosisTime) => (
                    <Tr key={diagnosisTime.id}>
                      <Td>{mapDayToThai(diagnosisTime.day)}</Td>
                      <Td>{formatTime(diagnosisTime.start_time)}</Td>
                      <Td>{formatTime(diagnosisTime.end_time)}</Td>
                      <Td minW={"160px"}>
                        {diagnosisTime.start_date
                          ? format(
                              diagnosisTime.start_date,
                              "EEEE ที่ dd MMMM yyyy",
                              { locale: th }
                            )
                          : "ไม่ระบุ"}
                      </Td>
                      <Td minW={"160px"}>
                        {diagnosisTime.end_date
                          ? format(
                              diagnosisTime.end_date,
                              "EEEE ที่ dd MMMM yyyy",
                              { locale: th }
                            )
                          : "ไม่ระบุ"}
                      </Td>
                      <Td minW={"160px"}>
                        <Button
                          my={0}
                          mr={1}
                          size={"sm"}
                          onClick={() => handleDiagnosisTimeEdit(diagnosisTime)}
                        >
                          แก้ไข
                        </Button>
                        <Button
                          my={0}
                          size={"sm"}
                          colorScheme="red"
                          onClick={() => openDeleteModal(diagnosisTime)}
                        >
                          ลบ
                        </Button>
                      </Td>
                    </Tr>
                  ))}

                {(!doctor ||
                  !doctor.diagnosisTimes ||
                  doctor.diagnosisTimes.length <= 0) && (
                  <Tr>
                    <Td colSpan={6}>ยังไม่มีข้อมูล</Td>
                  </Tr>
                )}
              </Tbody>
            </Table>
        )}
      </VStack>
      </Box>

      {/* Modal for Insertion/Editing */}
      <DiagnosisTimeModal
        isOpen={isModalOpen}
        operation={operation}
        fetchDoctorData={fetchDoctorData}
        onClose={() => {
          setIsModalOpen(false);
          setEditingDiagnosisTime(null);
        }}
        selectedTimeSlot={editingDiagnosisTime}
        doctorId={id}
      />

      <DiagnosisTimeDelete
        isDeleteModalOpen={isDeleteModalOpen}
        setDeleteModalOpen={setDeleteModalOpen}
        selectedDeleteTimetable={selectedDeleteDiagnosisTime}
        handleDeleteTimetable={handleDiagnosisTimeDelete}
      />
    </Box>
  );
};

export default DoctorTimeTablePage;
